import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import { toast } from 'react-toastify';
import { useMailEditorConfig } from './mailEditorConfig';
import { ALLOWED_IMAGE_TYPES, MAX_ASSET_BYTES } from './editorUtils';

const formatSize = bytes => {
  const value = Number(bytes) || 0;
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(value / 1024))} KB`;
};

const readAsBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const copyToClipboard = async (value, successMessage) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(successMessage);
  } catch (error) {
    toast.info('Kopieren nicht möglich — die URL steht im Feld darunter zum Markieren.');
  }
};

// One tile of the library: preview, file name, size and the URL actions.
const AssetTile = ({ asset, selected, onSelect, onCopy, onDelete, canDelete }) => (
  <Box
    onClick={() => onSelect(asset)}
    sx={{
      position: 'relative',
      border: '2px solid',
      borderColor: selected ? 'primary.main' : 'divider',
      borderRadius: 1,
      overflow: 'hidden',
      cursor: 'pointer',
      backgroundColor: 'background.paper',
      '&:hover': { borderColor: selected ? 'primary.main' : 'grey.500' },
      '&:hover .fz-asset-actions': { opacity: 1 },
    }}
  >
    <Box
      sx={{
        height: 110,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'grey.100',
        backgroundImage:
          'linear-gradient(45deg, #e0e0e0 25%, transparent 25%, transparent 75%, #e0e0e0 75%), linear-gradient(45deg, #e0e0e0 25%, transparent 25%, transparent 75%, #e0e0e0 75%)',
        backgroundSize: '16px 16px',
        backgroundPosition: '0 0, 8px 8px',
      }}
    >
      <Box
        component='img'
        src={asset.url}
        alt={asset.fileName}
        loading='lazy'
        sx={{ maxWidth: '100%', maxHeight: 110, display: 'block' }}
      />
    </Box>
    <Box sx={{ px: 1, py: 0.5 }}>
      <Typography variant='caption' noWrap title={asset.fileName} sx={{ display: 'block' }}>
        {asset.fileName}
      </Typography>
      <Typography variant='caption' color='text.secondary'>
        {formatSize(asset.size)}
      </Typography>
    </Box>
    <Box
      className='fz-asset-actions'
      onClick={event => event.stopPropagation()}
      sx={{
        position: 'absolute',
        top: 2,
        right: 2,
        display: 'flex',
        gap: 0.25,
        borderRadius: 1,
        backgroundColor: 'background.paper',
        boxShadow: 2,
        opacity: 0,
        transition: 'opacity 120ms',
      }}
    >
      <Tooltip title='Bild-URL kopieren'>
        <IconButton size='small' onClick={() => onCopy(asset)}>
          <ContentCopyIcon fontSize='inherit' />
        </IconButton>
      </Tooltip>
      {canDelete && (
        <Tooltip title='Aus der Ablage löschen'>
          <IconButton size='small' color='error' onClick={() => onDelete(asset)}>
            <DeleteOutlineIcon fontSize='inherit' />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  </Box>
);

/*
 * The mail image library ("Bildablage"): every image ever uploaded in a mail
 * editor, with its public URL. Images live in the same system as the mails, so
 * the URL is stable and can be pasted straight into an HTML element.
 *
 * onSelect(asset) is called with the chosen image; the caller decides what to do
 * with it (set the image element source, insert an <img> tag, …).
 */
const ImageLibraryDialog = ({ open, onClose, onSelect, selectedUrl, title = 'Bildablage' }) => {
  const { listAssets, uploadAsset, deleteAsset } = useMailEditorConfig();
  const fileInputRef = useRef(null);
  const [assets, setAssets] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(null);

  const load = useCallback(
    async (term = '') => {
      if (!listAssets) return;
      setLoading(true);
      try {
        const result = await listAssets({ search: term || undefined });
        if (result) setAssets(result);
      } catch (error) {
        toast.error('Fehler beim Laden der Bildablage');
      } finally {
        setLoading(false);
      }
    },
    [listAssets]
  );

  useEffect(() => {
    if (!open) return;
    setActive(null);
    load(search);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async event => {
    const file = event.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error('Ungültiger Dateityp. Erlaubt sind PNG, JPEG, GIF und WebP.');
      return;
    }
    if (file.size > MAX_ASSET_BYTES) {
      toast.error('Die Datei ist zu groß (maximal 1 MB).');
      return;
    }
    setUploading(true);
    try {
      const content = await readAsBase64(file);
      const result = await uploadAsset({ fileName: file.name, contentType: file.type, content });
      if (result?.url) {
        toast.success('Bild wurde in die Ablage geladen');
        setActive(result);
        await load(search);
      }
    } catch (error) {
      toast.error('Fehler beim Hochladen des Bildes');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async asset => {
    try {
      await deleteAsset(asset.assetId);
      toast.success('Bild wurde gelöscht');
      setActive(previous => (previous?.assetId === asset.assetId ? null : previous));
      await load(search);
    } catch (error) {
      if (error?.code === 'MAIL_ASSET_IN_USE') {
        // useHttp attaches the whole error body, the usage list sits in its data.
        const usage = Array.isArray(error.data?.data) ? error.data.data : [];
        const names = usage.map(entry => entry.name).filter(Boolean);
        toast.warning(
          `Bild wird noch verwendet${names.length ? ` (${names.slice(0, 3).join(', ')})` : ''} und wurde nicht gelöscht.`
        );
        return;
      }
      toast.error('Fehler beim Löschen des Bildes');
    }
  };

  const currentUrl = active?.url || selectedUrl || '';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant='contained'
            component='label'
            size='small'
            startIcon={uploading ? <CircularProgress size={16} color='inherit' /> : <UploadFileIcon />}
            disabled={uploading || !uploadAsset}
          >
            Bild hochladen
            <input
              ref={fileInputRef}
              type='file'
              hidden
              accept={ALLOWED_IMAGE_TYPES.join(',')}
              onChange={handleUpload}
            />
          </Button>
          <TextField
            size='small'
            placeholder='Nach Dateiname suchen'
            value={search}
            InputProps={{ startAdornment: <SearchIcon fontSize='small' sx={{ mr: 0.5 }} /> }}
            onChange={event => setSearch(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') load(search);
            }}
            onBlur={() => load(search)}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
        </Box>
        <Alert severity='info' sx={{ py: 0.25 }}>
          Alle Bilder liegen im FunZone-System und haben eine dauerhafte Adresse. Die URL lässt sich
          kopieren und direkt in ein HTML-Element einsetzen.
        </Alert>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && assets && assets.length === 0 && (
          <Typography variant='body2' color='text.secondary' sx={{ py: 4, textAlign: 'center' }}>
            {search ? 'Kein Bild gefunden.' : 'Noch keine Bilder in der Ablage.'}
          </Typography>
        )}
        {!loading && assets && assets.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: 1,
            }}
          >
            {assets.map(asset => (
              <AssetTile
                key={asset.assetId}
                asset={asset}
                selected={asset.url === currentUrl}
                canDelete={Boolean(deleteAsset)}
                onSelect={setActive}
                onCopy={entry => copyToClipboard(entry.url, 'Bild-URL kopiert')}
                onDelete={handleDelete}
              />
            ))}
          </Box>
        )}

        {currentUrl && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              size='small'
              label='Bild-URL'
              value={currentUrl}
              fullWidth
              InputProps={{ readOnly: true }}
              onFocus={event => event.target.select()}
            />
            <Button
              size='small'
              startIcon={<ContentCopyIcon />}
              sx={{ mt: 0.5, flexShrink: 0 }}
              onClick={() => copyToClipboard(currentUrl, 'Bild-URL kopiert')}
            >
              Kopieren
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant='contained'
          disabled={!active}
          onClick={() => {
            onSelect(active);
            onClose();
          }}
        >
          Übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageLibraryDialog;
